import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileSpreadsheet, Plus, Pencil, Archive, Copy, Download, Upload } from 'lucide-react';
import {
  PREFLIGHT_KEYS,
  PREFLIGHT_LABEL,
  STATIONS,
  stationLabel,
  type PreflightKey,
  type Station,
} from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import {
  useOnboardingForms,
  useCreateOnboardingForm,
  useCopyOnboardingForm,
  useArchiveOnboardingForm,
  useUpdateOnboardingForm,
  type OnboardingFormSummary,
} from '../../hooks/useOnboardingForms';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardSection } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonList } from '../../components/ui/Skeleton';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { FormEditor } from '../../components/onboarding/builder/FormEditor';

function slug(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

function currentVersion(form: OnboardingFormSummary): number | null {
  if (form.versions.length === 0) return null;
  return Math.max(...form.versions.map((v) => v.version));
}

/**
 * HR approval + pre-employment gate controls for one form. A form that satisfies a
 * pre-employment gate always needs sign-off (approving it is what flips the gate),
 * so the approval toggle is forced on and disabled in that case.
 */
function FormGateControls({ form }: { form: OnboardingFormSummary }) {
  const update = useUpdateOnboardingForm();
  const gated = !!form.preflight_key;
  const isHandover = form.kind === 'kit_handover';
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line/10 pt-3 text-sm">
      <label className="flex items-center gap-2 text-fg-muted">
        <span>Used as</span>
        <select
          value={form.kind}
          disabled={update.isPending}
          onChange={(e) =>
            update.mutate(
              e.target.value === 'kit_handover'
                ? { id: form.id, kind: 'kit_handover', station: form.station ?? 'Fleet' }
                : { id: form.id, kind: 'onboarding', station: null },
            )
          }
          className="rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-sm"
        >
          <option value="onboarding">Self-service onboarding task</option>
          <option value="kit_handover">Kit handover checklist (desk)</option>
        </select>
      </label>
      {isHandover ? (
        <label className="flex items-center gap-2 text-fg-muted">
          <span>Station</span>
          <select
            value={form.station ?? 'Fleet'}
            disabled={update.isPending}
            onChange={(e) => update.mutate({ id: form.id, station: e.target.value as Station })}
            className="rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-sm"
          >
            {STATIONS.map((s) => (
              <option key={s} value={s}>{stationLabel(s)}</option>
            ))}
          </select>
        </label>
      ) : (
        <>
          <label className="flex items-center gap-2 text-fg">
            <input
              type="checkbox"
              checked={form.requires_approval || gated}
              disabled={gated || update.isPending}
              onChange={(e) =>
                update.mutate({ id: form.id, requires_approval: e.target.checked })
              }
            />
            Needs HR sign-off
          </label>
          <label className="flex items-center gap-2 text-fg-muted">
            <span>Satisfies pre-employment</span>
            <select
              value={form.preflight_key ?? ''}
              disabled={update.isPending}
              onChange={(e) =>
                update.mutate({
                  id: form.id,
                  preflight_key: (e.target.value || null) as PreflightKey | null,
                })
              }
              className="rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-sm"
            >
              <option value="">None</option>
              {PREFLIGHT_KEYS.map((k) => (
                <option key={k} value={k}>{PREFLIGHT_LABEL[k]}</option>
              ))}
            </select>
          </label>
        </>
      )}
    </div>
  );
}

export function OnboardingForms() {
  const list = useOnboardingForms();
  const create = useCreateOnboardingForm();
  const copy = useCopyOnboardingForm();
  const archive = useArchiveOnboardingForm();

  const [title, setTitle] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<OnboardingFormSummary | null>(null);

  const forms = (list.data ?? []).filter((f) => !f.archived_at);
  const editingForm = forms.find((f) => f.id === editingId) ?? null;

  async function onCreate() {
    if (!title.trim()) return;
    try {
      const res = await create.mutateAsync({ key: slug(title), title: title.trim() });
      setTitle('');
      toast.success('Form created');
      setEditingId(res.form.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create form');
    }
  }

  async function onCopy(form: OnboardingFormSummary) {
    try {
      const res = await copy.mutateAsync(form.id);
      toast.success('Form copied — editing the draft');
      setEditingId(res.form.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not copy form');
    }
  }

  async function onArchive(form: OnboardingFormSummary) {
    try {
      await archive.mutateAsync(form.id);
      toast.success('Form archived');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not archive');
    }
    setConfirmArchive(null);
  }

  const importInput = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  async function onImportFile(file: File) {
    setImporting(true);
    try {
      const payload: unknown = JSON.parse(await file.text());
      await api('/api/admin/onboarding-forms/import', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      toast.success('Form imported and published');
      void list.refetch();
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : 'Could not import: not a valid form export file',
      );
    } finally {
      setImporting(false);
      if (importInput.current) importInput.current.value = '';
    }
  }

  return (
    <main className="mx-auto max-w-5xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={FileSpreadsheet}
        eyebrow="Onboarding"
        title={editingForm ? editingForm.title : 'Onboarding forms'}
        description={
          editingForm
            ? `Editing "${editingForm.key}". Publish to create a new version.`
            : 'Build the forms inductees fill in. Publishing creates an immutable version.'
        }
      />

      {editingForm ? (
        <FormEditor form={editingForm} onBack={() => setEditingId(null)} />
      ) : (
        <>
          <Card>
            <CardSection className="flex flex-wrap items-end gap-2">
              <label className="flex-1 text-sm">
                <span className="font-medium text-fg-muted">New form title</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. New Employee Details"
                  className="mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm"
                />
              </label>
              <Button variant="primary" size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={onCreate} loading={create.isPending} disabled={!title.trim()}>
                Create
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Upload className="h-4 w-4" />}
                loading={importing}
                onClick={() => importInput.current?.click()}
              >
                Import
              </Button>
              <input
                ref={importInput}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void onImportFile(file);
                }}
              />
            </CardSection>
          </Card>

          {list.isLoading ? (
            <SkeletonList rows={3} />
          ) : forms.length === 0 ? (
            <EmptyState icon={<FileSpreadsheet className="h-6 w-6" />} title="No forms yet" description="Create your first onboarding form above." />
          ) : (
            <div className="space-y-3">
              {forms.map((f) => {
                const v = currentVersion(f);
                return (
                  <Card key={f.id}>
                    <CardSection>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-fg">{f.title}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <Pill tone="neutral" size="sm">{f.key}</Pill>
                            {v ? <Pill tone="success" size="sm">v{v} published</Pill> : <Pill tone="warning" size="sm">Draft — no version</Pill>}
                            {f.kind === 'kit_handover' ? (
                              <Pill tone="accent" size="sm">
                                {f.station ? `${stationLabel(f.station)} handover` : 'Handover'}
                              </Pill>
                            ) : null}
                            {f.kind !== 'kit_handover' && (f.requires_approval || f.preflight_key) ? <Pill tone="info" size="sm">Sign-off</Pill> : null}
                            {f.preflight_key ? <Pill tone="accent" size="sm">Gate: {PREFLIGHT_LABEL[f.preflight_key]}</Pill> : null}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <Button variant="secondary" size="sm" iconLeft={<Pencil className="h-4 w-4" />} onClick={() => setEditingId(f.id)}>
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" iconLeft={<Copy className="h-4 w-4" />} onClick={() => onCopy(f)} loading={copy.isPending && copy.variables === f.id}>
                            Copy
                          </Button>
                          {v ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              iconLeft={<Download className="h-4 w-4" />}
                              onClick={() => {
                                window.location.href = `/api/admin/onboarding-forms/${f.id}/export`;
                              }}
                            >
                              Export
                            </Button>
                          ) : null}
                          <Button variant="ghost" size="sm" iconLeft={<Archive className="h-4 w-4" />} onClick={() => setConfirmArchive(f)}>
                            Archive
                          </Button>
                        </div>
                      </div>
                      <FormGateControls form={f} />
                    </CardSection>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <ConfirmModal
        open={!!confirmArchive}
        title="Archive form?"
        message={confirmArchive ? `"${confirmArchive.title}" will be hidden. Existing assignments keep working.` : ''}
        tone="warning"
        confirmLabel="Archive"
        onConfirm={() => {
          if (confirmArchive) return onArchive(confirmArchive);
        }}
        onClose={() => setConfirmArchive(null)}
      />
    </main>
  );
}
